import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../../../utilities/appToast';
import {
    createPublicacionApi,
    createImageUrl,
    getFormatoExcel
} from '../../../../api/axios';
import { base64ToBlob, saveByteArrayXlsx } from '../../../../utilities/formatFile';
import {
  getSellerDesplegable
} from '../../../../actions/sellersActions';
import {
  fetchCategorias,
  fetchMarcas,
  fetchTiposAlicuotasIva,
  fetchTiposPublicacion
} from '../../../../actions/publicacionesActions';
import './CreacionMasivaWizard.css';

const ADMIN_TYPES = [1, 2];
const HIDDEN_SELLERS = [1, 3, 4, 5, 6];
const STEP_LABELS = ['Actualizacion', 'Lote de actualizacion', 'Revision', 'Resultado'];
const IMAGE_MAX_SIZE = 512000;
const ROWS_PER_PAGE = 25;
const DEFAULT_ZONAS = {
    amba: true,
    interior: true,
    patagonia: true
};

const CREACION_MASIVA_TEMPLATE_NAME = 'Modelo Creación Masiva de Publicaciones';

const createRowId = () => `row-${Date.now()}-${Math.round(Math.random() * 100000)}`;

const createDefaultDimensions = () => ({
    alto: '1',
    ancho: '1',
    profundidad: '1',
    peso: '1'
});

const createDefaultCostosFee = () => ({
    amba: '',
    interior: '',
    patagonia: ''
});

const ROW_ERROR_FIELD_LABELS = {
    skuSeller: 'SKU seller',
    titulo: 'Titulo',
    precioLista: 'Precio lista',
    precioPromocional: 'Precio promo',
    stock: 'Stock',
    imagenes: 'Imagenes',
    alto: 'Alto',
    ancho: 'Ancho',
    profundidad: 'Profundidad',
    peso: 'Peso',
    fee_amba: 'Costo fee AMBA',
    fee_interior: 'Costo fee Interior',
    fee_patagonia: 'Costo fee Patagonia'
};

const createEmptyRow = (source = 'manual') => ({
    id: createRowId(),
    source,
    skuSeller: '',
    titulo: '',
    ean: '',
    descripcionAmpliada: '',
    precioLista: '',
    precioPromocional: '',
    stock: '0',
    codigoLegibilidad: '',
    linkYoutube: '',
    imagenesTexto: '',
    imageFiles: [],
    dimensiones: createDefaultDimensions(),
    zonasEnvioGratis: { ...DEFAULT_ZONAS },
    costosFee: createDefaultCostosFee(),
    errors: {}
});

const createInitialDefaults = (fallbackSellerId) => ({
    idSeller: fallbackSellerId ?? '',
    categoria: '',
    marca: '',
    idTipoPublicacion: '',
    alicuotaIva: '',
    alicuotaImpuestoInterno: '0',
    observacion: '',
    descripcionAmpliada: '',
    tipoEntrega: '1',
    envioGratis: true,
    envioPropio: false,
    garantiaExtActiva: false,
    garantiaFabrica: '0',
    replicateDimensions: true,
    alto: '1',
    ancho: '1',
    profundidad: '1',
    peso: '1'
});

const normalizeNumericString = (value) => {
    const rawValue = String(value ?? '').trim();
    if (rawValue === '') return '';

    const compactValue = rawValue.replace(/\s+/g, '');

    if (/^-?\d{1,3}(\.\d{3})*,\d+$/.test(compactValue)) {
        return compactValue.replace(/\./g, '').replace(',', '.');
    }

    if (/^-?\d{1,3}(,\d{3})*\.\d+$/.test(compactValue)) {
        return compactValue.replace(/,/g, '');
    }

    if (/^-?\d+,\d+$/.test(compactValue)) {
        return compactValue.replace(',', '.');
    }

    return compactValue.replace(',', '.');
};

const toNumber = (value, fallback = 0) => {
    if (value == null || String(value).trim() === '') return fallback;

    const numericValue = Number(normalizeNumericString(value));
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeSellerOptions = (response) => {
    const data = response ?? {};
    const rawList =
        Array.isArray(data?.content?.[0]?.datosSellerDesplegables) ? data.content[0].datosSellerDesplegables
            : Array.isArray(data?.Content?.[0]?.DatosSellerDesplegables) ? data.Content[0].DatosSellerDesplegables
                : Array.isArray(data?.elementos) ? data.elementos
                    : Array.isArray(data) ? data
                        : [];

    return rawList
        .map((seller) => ({
            value: seller?.idSeller ?? seller?.IdSeller ?? seller?.codigo ?? seller?.Codigo ?? 0,
            label: seller?.razonSocial ?? seller?.RazonSocial ?? seller?.descripcion ?? seller?.Descripcion ?? ''
        }))
        .filter((seller) => seller.value && seller.label)
        .filter((seller) => !HIDDEN_SELLERS.includes(Number(seller.value)));
};

const getRowErrors = (rows, defaults) => {
    const skuCounter = {};

    rows.forEach((row) => {
        const normalizedSku = String(row?.skuSeller ?? '').trim().toUpperCase();
        if (!normalizedSku) return;
        skuCounter[normalizedSku] = (skuCounter[normalizedSku] ?? 0) + 1;
    });

    return rows.map((row) => {
        const errors = {};
        const skuSeller = String(row?.skuSeller ?? '').trim();
        const titulo = String(row?.titulo ?? '').trim();
        const precioLista = toNumber(row?.precioLista, NaN);
        const precioPromocional = String(row?.precioPromocional ?? '').trim() === ''
            ? null
            : toNumber(row?.precioPromocional, NaN);
        const stock = toNumber(row?.stock, NaN);
        const normalizedSku = skuSeller.toUpperCase();

        if (!skuSeller) errors.skuSeller = 'Ingresa el SKU seller.';
        if (skuSeller && skuCounter[normalizedSku] > 1) errors.skuSeller = 'El SKU esta repetido en el lote.';
        if (!titulo) errors.titulo = 'Ingresa el titulo.';
        if (!Number.isFinite(precioLista) || precioLista <= 0) errors.precioLista = 'Ingresa un precio valido.';
        if (precioPromocional != null && (!Number.isFinite(precioPromocional) || precioPromocional < 0)) {
            errors.precioPromocional = 'Ingresa un precio promocional valido.';
        }
        if (
            precioPromocional != null &&
            Number.isFinite(precioPromocional) &&
            Number.isFinite(precioLista) &&
            precioPromocional > precioLista
        ) {
            errors.precioPromocional = 'El precio promocional no puede superar al precio de lista.';
        }
        if (!Number.isFinite(stock) || stock < 0) errors.stock = 'Ingresa un stock valido.';
        const imageCount = getRowImageCount(row);
        if (imageCount === 0) {
            errors.imagenes = 'Debe especificar al menos una imagen de la publicacion.';
        }
        if (imageCount > 5) {
            errors.imagenes = 'Cada publicacion admite hasta 5 imagenes.';
        }

        if (defaults?.envioPropio) {
            ['amba', 'interior', 'patagonia'].forEach((zone) => {
                const feeValue = String(row?.costosFee?.[zone] ?? '').trim();
                if (feeValue === '' || !Number.isFinite(toNumber(feeValue, NaN)) || toNumber(feeValue, NaN) < 0) {
                    errors[`fee_${zone}`] = 'Completa el costo fee de la zona.';
                }
            });
        }

        if (!defaults?.replicateDimensions) {
            const dimensiones = row?.dimensiones ?? {};
            ['alto', 'ancho', 'profundidad', 'peso'].forEach((field) => {
                const numericValue = toNumber(dimensiones[field], NaN);
                if (!Number.isFinite(numericValue) || numericValue <= 0) {
                    errors[field] = 'Completa dimensiones validas para la fila.';
                }
            });
        }

        return errors;
    });
};

const getContextErrors = (defaults, selectedSellerId) => {
    const errors = {};

    if (!selectedSellerId) errors.idSeller = 'Selecciona un seller.';
    if (!defaults.categoria) errors.categoria = 'Selecciona una categoria.';
    if (!defaults.marca) errors.marca = 'Selecciona una marca.';
    if (!defaults.idTipoPublicacion) errors.idTipoPublicacion = 'Selecciona un tipo de publicacion.';
    if (defaults.alicuotaIva === '' || defaults.alicuotaIva == null) errors.alicuotaIva = 'Selecciona una alicuota IVA.';

    return errors;
};

const getSummary = (rows, defaults) => {
    const errors = getRowErrors(rows, defaults);
    const conErrores = errors.filter((rowErrors) => Object.keys(rowErrors).length > 0).length;

    return {
        total: rows.length,
        validas: rows.length - conErrores,
        conErrores
    };
};

const parseImageUrls = (rawValue) => {
    return String(rawValue ?? '')
        .split(/\r?\n/)
        .map((item) => item.trim())
        .filter((item) => item !== '');
};

const getRowImageCount = (row) => parseImageUrls(row?.imagenesTexto).length + (row?.imageFiles?.length ?? 0);

const isMeaningfulValue = (value) => String(value ?? '').trim() !== '';

const isRowBlank = (row) => {
    if (!row) return true;

    const hasTypedValues = [
        row?.skuSeller,
        row?.titulo,
        row?.ean,
        row?.descripcionAmpliada,
        row?.precioLista,
        row?.precioPromocional,
        row?.codigoLegibilidad,
        row?.linkYoutube,
        row?.imagenesTexto,
        row?.costosFee?.amba,
        row?.costosFee?.interior,
        row?.costosFee?.patagonia
    ].some(isMeaningfulValue);

    const hasCustomStock = String(row?.stock ?? '').trim() !== '' && String(row?.stock ?? '').trim() !== '0';
    const hasFiles = (row?.imageFiles?.length ?? 0) > 0;

    return !hasTypedValues && !hasCustomStock && !hasFiles;
};

const getRowIssueDetails = (rowErrors) => Object.entries(rowErrors ?? {})
    .filter(([, message]) => String(message ?? '').trim() !== '')
    .map(([field, message]) => ({
        field,
        label: ROW_ERROR_FIELD_LABELS[field] ?? field,
        message: String(message).trim()
    }));

const normalizeImportedNumericValue = (value, fallback = '') => {
    const rawValue = String(value ?? '').trim();
    if (rawValue === '') return fallback;

    if (/^-?\d{1,3}(,\d{3})+$/.test(rawValue)) {
        return rawValue.replace(/,/g, '');
    }

    if (/^-?\d{1,3}(\.\d{3})+$/.test(rawValue)) {
        return rawValue.replace(/\./g, '');
    }

    return normalizeNumericString(rawValue);
};

const normalizeHeader = (value) => String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_.-]+/g, '');

const getFirstRecord = (rawValue) => {
    let record = '';
    let inQuotes = false;

    for (let index = 0; index < rawValue.length; index += 1) {
        const currentChar = rawValue[index];
        const nextChar = rawValue[index + 1];

        if (currentChar === '"') {
            if (inQuotes && nextChar === '"') {
                record += currentChar;
                index += 1;
            } else {
                inQuotes = !inQuotes;
                record += currentChar;
            }
            continue;
        }

        if ((currentChar === '\n' || currentChar === '\r') && !inQuotes) {
            break;
        }

        record += currentChar;
    }

    return record;
};

const detectDelimiter = (rawValue) => {
    const firstRecord = getFirstRecord(rawValue);
    const candidates = ['\t', ';', ','];
    const selected = candidates
        .map((candidate) => ({
            candidate,
            count: firstRecord.split(candidate).length - 1
        }))
        .sort((left, right) => right.count - left.count)[0];

    return selected?.count > 0 ? selected.candidate : ';';
};

const parseDelimitedText = (rawValue, delimiter) => {
    const rows = [];
    let currentRow = [];
    let currentField = '';
    let inQuotes = false;

    const pushField = () => {
        currentRow.push(currentField.trim());
        currentField = '';
    };

    const pushRow = () => {
        if (currentRow.some((field) => field !== '')) {
            rows.push(currentRow);
        }
        currentRow = [];
    };

    for (let index = 0; index < rawValue.length; index += 1) {
        const currentChar = rawValue[index];
        const nextChar = rawValue[index + 1];

        if (currentChar === '"') {
            if (inQuotes && nextChar === '"') {
                currentField += '"';
                index += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }

        if (currentChar === delimiter && !inQuotes) {
            pushField();
            continue;
        }

        if ((currentChar === '\n' || currentChar === '\r') && !inQuotes) {
            if (currentChar === '\r' && nextChar === '\n') {
                index += 1;
            }
            pushField();
            pushRow();
            continue;
        }

        currentField += currentChar;
    }

    pushField();
    pushRow();

    return rows;
};

const parseBooleanLike = (value) => {
    const normalized = String(value ?? '').trim().toLowerCase();
    return ['1', 'si', 'sí', 'true', 'x', 'yes', 'y'].includes(normalized);
};

const normalizeApiPayload = (data) => {
    if (data == null || data === '') return data;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch {
            return data;
        }
    }
    return data;
};

const formatApiValue = (value, fallback = '-') => {
    if (value == null) return fallback;

    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed === '' ? fallback : trimmed;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (Array.isArray(value)) {
        const items = value
            .map((item) => formatApiValue(item, ''))
            .filter((item) => item !== '');
        return items.length > 0 ? items.join(' | ') : fallback;
    }

    if (typeof value === 'object') {
        const tupleMessage = [
            value.item1, value.Item1,
            value.item2, value.Item2,
            value.item3, value.Item3,
            value.item4, value.Item4
        ].filter((item) => typeof item === 'string' && item.trim() !== '').join(' - ');
        if (tupleMessage) return tupleMessage;

        const preferredValue = value.errorMesage ?? value.ErrorMesage ?? value.message ?? value.Message ??
            value.descripcion ?? value.Descripcion ?? value.skuSeller ?? value.SkuSeller ??
            value.skuMarket ?? value.SkuMarket ?? value.idPublicacion ?? value.IdPublicacion;
        if (preferredValue != null && typeof preferredValue !== 'object') {
            return formatApiValue(preferredValue, fallback);
        }

        try {
            return JSON.stringify(value);
        } catch {
            return fallback;
        }
    }

    return fallback;
};

const toastApiList = (items, level = 'warn') => {
    if (!Array.isArray(items) || items.length === 0) return false;

    items.forEach((item) => {
        const message = formatApiValue(normalizeApiPayload(item), '');
        if (message) {
            toast[level](message);
        }
    });

    return true;
};

const getNormalizedSku = (value) => String(value ?? '').trim().toUpperCase();

const cloneRowsSnapshot = (rows) => (Array.isArray(rows) ? rows : []).map((row) => ({
    ...row,
    imageFiles: [...(row?.imageFiles ?? [])],
    dimensiones: { ...(row?.dimensiones ?? createDefaultDimensions()) },
    zonasEnvioGratis: { ...(row?.zonasEnvioGratis ?? DEFAULT_ZONAS) },
    costosFee: { ...(row?.costosFee ?? createDefaultCostosFee()) },
    errors: { ...(row?.errors ?? {}) }
}));

const getRowsMeta = (rowsSnapshot) => (Array.isArray(rowsSnapshot) ? rowsSnapshot : []).map((row, index) => ({
    rowId: row?.id ?? null,
    rowNumber: index + 1,
    skuSeller: String(row?.skuSeller ?? '').trim(),
    skuSellerNormalized: getNormalizedSku(row?.skuSeller),
    titulo: String(row?.titulo ?? '').trim()
}));

const getApiErrorCandidateSku = (item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return '';

    const directSku = item?.skuSeller ?? item?.SkuSeller;
    if (String(directSku ?? '').trim() !== '') return String(directSku).trim();

    const tupleValues = [item?.item1, item?.Item1, item?.item2, item?.Item2];
    return tupleValues.find((value) => String(value ?? '').trim() !== '') ?? '';
};

const enrichApiErrorsWithRowContext = (items, rowsSnapshot, createdItems = []) => {
    const rowsMeta = getRowsMeta(rowsSnapshot);
    const availableIndexes = rowsMeta.map((_, index) => index);
    const createdSkus = new Set(
        (Array.isArray(createdItems) ? createdItems : [])
            .map((item) => {
                const normalizedItem = normalizeApiPayload(item);
                return getNormalizedSku(normalizedItem?.skuSeller ?? normalizedItem?.SkuSeller);
            })
            .filter((value) => value !== '')
    );

    if (createdSkus.size > 0) {
        for (let index = availableIndexes.length - 1; index >= 0; index -= 1) {
            const rowMetaIndex = availableIndexes[index];
            if (createdSkus.has(rowsMeta[rowMetaIndex]?.skuSellerNormalized)) {
                availableIndexes.splice(index, 1);
            }
        }
    }

    return (Array.isArray(items) ? items : []).map((rawItem, index) => {
        const normalizedItem = normalizeApiPayload(rawItem);
        const baseItem = normalizedItem && typeof normalizedItem === 'object' && !Array.isArray(normalizedItem)
            ? { ...normalizedItem }
            : { message: normalizedItem };
        let matchedIndex = -1;
        let matchType = null;

        const explicitRowNumber = Number.parseInt(
            String(baseItem?.rowNumber ?? baseItem?.RowNumber ?? baseItem?.fila ?? baseItem?.Fila ?? ''),
            10
        );

        if (Number.isFinite(explicitRowNumber) && explicitRowNumber >= 1 && explicitRowNumber <= rowsMeta.length) {
            matchedIndex = explicitRowNumber - 1;
            matchType = 'rowNumber';
        } else {
            const candidateSku = getNormalizedSku(getApiErrorCandidateSku(baseItem));
            if (candidateSku !== '') {
                matchedIndex = rowsMeta.findIndex((rowMeta) => rowMeta.skuSellerNormalized === candidateSku);
                if (matchedIndex >= 0) {
                    matchType = 'sku';
                }
            }
        }

        if (matchedIndex === -1) {
            matchedIndex = availableIndexes[index] ?? availableIndexes[0] ?? index;
            if (rowsMeta[matchedIndex]) {
                matchType = 'inferred';
            }
        }

        const availablePosition = availableIndexes.indexOf(matchedIndex);
        if (availablePosition >= 0) {
            availableIndexes.splice(availablePosition, 1);
        }

        const rowMeta = rowsMeta[matchedIndex];

        return {
            ...baseItem,
            __rowId: rowMeta?.rowId ?? null,
            __rowNumber: rowMeta?.rowNumber ?? null,
            __rowSku: rowMeta?.skuSeller ?? '',
            __rowTitle: rowMeta?.titulo ?? '',
            __rowMatchType: matchType
        };
    });
};

const getResultErrorTitle = (item, index) => {
    return getResultErrorHeading(item, index);
    const labelBase = item?.__rowNumber
        ? `Fila ${item.__rowNumber}${item?.__rowMatchType === 'inferred' ? ' (estimada)' : ''}`
        : `Error ${index + 1}`;
    const sku = formatApiValue(item?.skuSeller ?? item?.SkuSeller ?? item?.__rowSku, '');
    return sku ? `${labelBase} · SKU ${sku}` : labelBase;
};

const getResultErrorContext = (item) => {
    const title = formatApiValue(item?.titulo ?? item?.Titulo ?? item?.__rowTitle, '');
    return title ? `Titulo: ${title}` : '';
};

const getResultErrorHeading = (item, index) => {
    const labelBase = item?.__rowNumber
        ? `Fila ${item.__rowNumber}${item?.__rowMatchType === 'inferred' ? ' (estimada)' : ''}`
        : `Error ${index + 1}`;
    const sku = formatApiValue(item?.skuSeller ?? item?.SkuSeller ?? item?.__rowSku, '');
    return sku ? `${labelBase} | SKU ${sku}` : labelBase;
};

const toastWizardCreationSummary = (normalizedCreated, normalizedErrors) => {
    const ok = normalizedCreated.length;
    const bad = normalizedErrors.length;
    const errToastLimit = 12;

    const toastErrLines = (errors) => {
        errors.slice(0, errToastLimit).forEach((item, index) => {
            toast.warn(
                `${getResultErrorTitle(item, index)}: ${formatApiValue(
                    item?.errorMesage ?? item?.ErrorMesage ?? item?.message ?? item?.Message ?? item,
                    'Sin detalle'
                )}`
            );
        });
        if (errors.length > errToastLimit) {
            toast.warn(`…y ${errors.length - errToastLimit} error(es) más en el paso Resultado.`);
        }
    };

    if (ok > 0 && bad === 0) {
        toast.success(`${ok} publicación(es) creada(s) correctamente.`);
        return;
    }
    if (ok > 0 && bad > 0) {
        toast.success(`${ok} publicación(es) creada(s) correctamente.`);
        toast.warn(`${bad} fila(s) con error (detalle también en el paso Resultado).`);
        toastErrLines(normalizedErrors);
        return;
    }
    toast.error(`No se creó ninguna publicación (${bad} error(es)).`);
    toastErrLines(normalizedErrors);
};

const CreacionMasivaWizard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const tipoUsuario = useSelector((state) => state.general.tipoUsuario);
    const currentSellerId = useSelector((state) => state.general.idSeller);
    const userId = useSelector((state) => state.general.userId);
    const categorias = useSelector((state) => state.publicaciones.categorias ?? []);
    const marcas = useSelector((state) => state.publicaciones.marcas ?? []);
    const tiposPublicacion = useSelector((state) => state.publicaciones.tiposPublicacion ?? []);
    const tiposAlicuotaIva = useSelector((state) => state.publicaciones.tiposAlicuotaIva ?? []);

    const isPrivilegedUser = ADMIN_TYPES.includes(Number(tipoUsuario));

    const [step, setStep] = useState(0);
    const [defaults, setDefaults] = useState(createInitialDefaults(currentSellerId));
    const [selectedSellerId, setSelectedSellerId] = useState(
        isPrivilegedUser ? '' : String(currentSellerId ?? '')
    );
    const [sellerSearch, setSellerSearch] = useState('');
    const [sellerOptions, setSellerOptions] = useState([]);
    const [isLoadingSellers, setIsLoadingSellers] = useState(false);
    const [categoriaSearch, setCategoriaSearch] = useState('');
    const [marcaSearch, setMarcaSearch] = useState('');
    const [rows, setRows] = useState([]);
    const [pasteValue, setPasteValue] = useState('');
    const [contextErrors, setContextErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentRowsPage, setCurrentRowsPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState({});
    const [result, setResult] = useState({
        created: [],
        errors: []
    });
    const excelInputRef = useRef(null);
    const [excelDragDepth, setExcelDragDepth] = useState(0);
    const [excelJustLoaded, setExcelJustLoaded] = useState(false);

    useEffect(() => {
        dispatch(fetchTiposAlicuotasIva());
        dispatch(fetchCategorias(''));
        dispatch(fetchMarcas(''));
    }, [dispatch]);

    useEffect(() => {
        if (!isPrivilegedUser) {
            setSelectedSellerId(String(currentSellerId ?? ''));
            setDefaults((prevState) => ({
                ...prevState,
                idSeller: currentSellerId ?? ''
            }));
        }
    }, [currentSellerId, isPrivilegedUser]);

    useEffect(() => {
        if (selectedSellerId) {
            dispatch(fetchTiposPublicacion(selectedSellerId));
        }
    }, [dispatch, selectedSellerId]);

    useEffect(() => {
        if (isPrivilegedUser && sellerOptions.length === 0) {
            loadSellers('');
        }
    }, [isPrivilegedUser, sellerOptions.length]);

    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
        setCurrentRowsPage((previousPage) => Math.min(previousPage, totalPages));
    }, [rows.length]);

    const loadSellers = async (searchTerm = '') => {
        setIsLoadingSellers(true);

        try {
            const response = await getSellerDesplegable({
                descripcion: searchTerm,
                page: 1,
                pageSize: 25
            });
            setSellerOptions(normalizeSellerOptions(response));
        } catch (error) {
            setSellerOptions([]);
            toast.warn('No se pudieron cargar sellers.');
        } finally {
            setIsLoadingSellers(false);
        }
    };

    const handleDefaultsChange = (event) => {
        const { name, value, type, checked } = event.target;
        setDefaults((prevState) => ({
            ...prevState,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleRowChange = (rowId, field, value) => {
        setRows((prevState) => prevState.map((row) => (
            row.id === rowId
                ? {
                    ...row,
                    [field]: value,
                    errors: {
                        ...row.errors,
                        [field]: undefined,
                        ...(field === 'imagenesTexto' ? { imagenes: undefined } : {})
                    }
                }
                : row
        )));
    };

    const handleRowDimensionChange = (rowId, field, value) => {
        setRows((prevState) => prevState.map((row) => (
            row.id === rowId
                ? {
                    ...row,
                    dimensiones: {
                        ...row.dimensiones,
                        [field]: value
                    }
                }
                : row
        )));
    };

    const handleRowZonaChange = (rowId, zone, checked) => {
        setRows((prevState) => prevState.map((row) => (
            row.id === rowId
                ? {
                    ...row,
                    zonasEnvioGratis: {
                        ...(row.zonasEnvioGratis ?? DEFAULT_ZONAS),
                        [zone]: checked
                    }
                }
                : row
        )));
    };

    const handleRowFeeChange = (rowId, zone, value) => {
        setRows((prevState) => prevState.map((row) => (
            row.id === rowId
                ? {
                    ...row,
                    costosFee: {
                        ...(row.costosFee ?? createDefaultCostosFee()),
                        [zone]: value
                    }
                }
                : row
        )));
    };

    const handleRowFilesChange = (rowId, filesList) => {
        const files = Array.from(filesList ?? []);
        const invalidFile = files.find((file) => !file.type?.includes('image') || file.size > IMAGE_MAX_SIZE);

        if (invalidFile) {
            toast.warn('Cada imagen debe ser un archivo de imagen menor a 500 KB.');
            return;
        }

        const targetRow = rows.find((row) => row.id === rowId);
        const currentCount = getRowImageCount(targetRow);
        if ((currentCount + files.length) > 5) {
            toast.warn('Cada publicacion admite hasta 5 imagenes entre URLs y archivos.');
            return;
        }

        setRows((prevState) => prevState.map((row) => (
            row.id === rowId
                ? {
                    ...row,
                    imageFiles: [...(row.imageFiles ?? []), ...files],
                    errors: {
                        ...row.errors,
                        imagenes: undefined
                    }
                }
                : row
        )));
    };

    const removeRowFile = (rowId, fileIndex) => {
        setRows((prevState) => prevState.map((row) => (
            row.id === rowId
                ? {
                    ...row,
                    imageFiles: (row.imageFiles ?? []).filter((_, index) => index !== fileIndex),
                    errors: {
                        ...row.errors,
                        imagenes: undefined
                    }
                }
                : row
        )));
    };

    const toggleRowExpanded = (rowId) => {
        setExpandedRows((prevState) => ({
            ...prevState,
            [rowId]: !prevState[rowId]
        }));
    };

    const addEmptyRow = () => {
        setRows((prevState) => [...prevState, createEmptyRow()]);
        setCurrentRowsPage(Math.max(1, Math.ceil((rows.length + 1) / ROWS_PER_PAGE)));
    };

    const duplicateRow = (rowId) => {
        const rowToDuplicate = rows.find((row) => row.id === rowId);
        if (!rowToDuplicate) return;

        setRows((prevState) => [
            ...prevState,
            {
                ...rowToDuplicate,
                id: createRowId(),
                source: 'manual',
                skuSeller: '',
                errors: {}
            }
        ]);
    };

    const removeRow = (rowId) => {
        setRows((prevState) => prevState.filter((row) => row.id !== rowId));
        setExpandedRows((currentState) => {
            const nextState = { ...currentState };
            delete nextState[rowId];
            return nextState;
        });
    };

    const clearRowsLote = () => {
        setStep(1);
        setRows([]);
        setCurrentRowsPage(1);
        setExpandedRows({});
        setPasteValue('');
        setResult({ created: [], errors: [] });
        toast.info('Se limpio el lote para que puedas cargar otro archivo.');
    };

    const removeCsvRows = () => {
        const remainingRows = rows.filter((row) => row?.source !== 'csv');
        if (remainingRows.length === rows.length) {
            toast.info('No hay filas importadas desde CSV para eliminar.');
            return;
        }

        setStep(1);
        setRows(remainingRows);
        setCurrentRowsPage(1);
        setExpandedRows({});
        setPasteValue('');
        setResult({ created: [], errors: [] });
        toast.info('Se eliminaron las filas agregadas desde CSV.');
    };

    const returnToRowsForCorrection = () => {
        const failedRows = errorResults
            .map((item) => item?.__rowId)
            .filter((rowId) => rowId);
        const inferredMatch = errorResults.some((item) => item?.__rowMatchType === 'inferred');
        const canKeepOnlyFailedRows = failedRows.length > 0 && (!inferredMatch || createdResults.length === 0);
        const nextRows = canKeepOnlyFailedRows
            ? rows.filter((row) => failedRows.includes(row.id))
            : rows;
        const nextExpandedRows = Object.fromEntries(
            nextRows
                .filter((row) => failedRows.includes(row.id))
                .slice(0, 8)
                .map((row) => [row.id, true])
        );
        const firstFailedRowNumber = errorResults
            .map((item) => item?.__rowNumber)
            .find((rowNumber) => Number.isFinite(rowNumber));

        setRows(nextRows);
        setCurrentRowsPage(firstFailedRowNumber ? Math.max(1, Math.ceil(firstFailedRowNumber / ROWS_PER_PAGE)) : 1);
        setExpandedRows(nextExpandedRows);
        setResult({ created: [], errors: [] });
        setStep(1);

        if (canKeepOnlyFailedRows) {
            toast.info('Dejamos solo las filas con error para que puedas corregirlas y reenviar el lote.');
            return;
        }

        toast.info('Volvimos al lote completo para corregirlo. Algunas filas quedaron identificadas de forma estimada.');
    };

    const applyRowErrors = (rowErrors) => {
        setRows((prevState) => prevState.map((row, index) => ({
            ...row,
            errors: rowErrors[index] ?? {}
        })));
    };

    const validateContextStep = () => {
        const errors = getContextErrors(defaults, selectedSellerId);
        setContextErrors(errors);
        return errors;
    };

    const validateRowsStep = () => {
        if (rows.length === 0) {
            setExpandedRows({});
            return [];
        }

        const rowErrors = getRowErrors(rows, defaults);
        applyRowErrors(rowErrors);
        return rowErrors;
    };

    const prepareReview = () => {
        const currentContextErrors = validateContextStep();
        if (Object.keys(currentContextErrors).length > 0) {
            toast.warn('Completa el contexto del lote antes de continuar.');
            return false;
        }

        if (rows.length === 0) {
            toast.warn('Agrega al menos una fila al lote antes de continuar.');
            return false;
        }

        const rowErrors = validateRowsStep();
        const hasRowErrors = rowErrors.some((rowError) => Object.keys(rowError).length > 0);

        if (hasRowErrors) {
            const firstErrorIndex = rowErrors.findIndex((rowError) => Object.keys(rowError).length > 0);
            const rowsWithErrors = rows
                .filter((_, index) => Object.keys(rowErrors[index] ?? {}).length > 0)
                .slice(0, 8);

            if (firstErrorIndex >= 0) {
                setCurrentRowsPage(Math.floor(firstErrorIndex / ROWS_PER_PAGE) + 1);
            }

            setExpandedRows(Object.fromEntries(rowsWithErrors.map((row) => [row.id, true])));
            toast.warn('Hay datos pendientes o invalidos en el lote. Revisa la columna Estado de cada fila.');
            return false;
        }

        return true;
    };

    const importFromRecords = (records, sourceLabel = 'CSV') => {
        const importedRows = [];
        const firstValues = records[0];
        const normalizedHeaders = firstValues.map(normalizeHeader);
        const hasHeader = normalizedHeaders.some((header) => [
            'idseller', 'skuseller', 'sku', 'titulo', 'preciolista', 'precio', 'imagen1', 'img1', 'feeamba', 'costofeeamba'
        ].includes(header));

        const headerLookup = normalizedHeaders.reduce((accumulator, header, index) => {
            accumulator[header] = [...(accumulator[header] ?? []), index];
            return accumulator;
        }, {});

        const getValuesByAliases = (values, aliases) => aliases.flatMap((alias) => {
            const indexes = headerLookup[alias] ?? [];
            return indexes.map((index) => values[index] ?? '');
        });

        const getFirstValueByAliases = (values, aliases, fallback = '') => {
            const candidate = getValuesByAliases(values, aliases)
                .map((item) => String(item ?? '').trim())
                .find((item) => item !== '');
            return candidate ?? fallback;
        };

        const getFirstValueFromRecords = (aliases, fallback = '') => {
            for (const record of records.slice(hasHeader ? 1 : 0)) {
                const value = getFirstValueByAliases(record, aliases, '');
                if (value !== '') return value;
            }

            return fallback;
        };

        const hasAnyHeader = (aliases) => aliases.some((alias) => (headerLookup[alias] ?? []).length > 0);
        const dataRecords = hasHeader ? records.slice(1) : records;

        dataRecords.forEach((values) => {
            if (!Array.isArray(values) || values.every((value) => String(value ?? '').trim() === '')) return;

            const row = createEmptyRow('csv');

            if (hasHeader) {
                const imageHeaders = [
                    ['imagen1', 'img1'],
                    ['imagen2', 'img2'],
                    ['imagen3', 'img3'],
                    ['imagen4', 'img4'],
                    ['imagen5', 'img5']
                ];
                const imageUrls = imageHeaders
                    .map((aliases) => getFirstValueByAliases(values, aliases, ''))
                    .filter((item) => item !== '')
                    .slice(0, 5);

                row.skuSeller = getFirstValueByAliases(values, ['skuseller', 'sku']);
                row.titulo = getFirstValueByAliases(values, ['titulo', 'nombre', 'title']);
                row.ean = getFirstValueByAliases(values, ['ean']);
                row.descripcionAmpliada = getFirstValueByAliases(values, ['descripcionampliada', 'descripcionlarga', 'descripcion']);
                row.precioLista = normalizeImportedNumericValue(getFirstValueByAliases(values, ['preciolista', 'precio']));
                row.precioPromocional = normalizeImportedNumericValue(getFirstValueByAliases(values, ['preciopromocional', 'preciopromo', 'promocional']));
                row.stock = normalizeImportedNumericValue(getFirstValueByAliases(values, ['stock'], '0'), '0');
                row.codigoLegibilidad = getFirstValueByAliases(values, ['codigolegibilidad', 'legibilidad']);
                row.linkYoutube = getFirstValueByAliases(values, ['linkyoutube', 'youtube', 'video']);
                row.imagenesTexto = imageUrls.join('\n');
                row.costosFee = {
                    amba: normalizeImportedNumericValue(getFirstValueByAliases(values, ['costofeeamba', 'feeamba', 'costoamba'])),
                    interior: normalizeImportedNumericValue(getFirstValueByAliases(values, ['costofeeinterior', 'feeinterior', 'costointerior'])),
                    patagonia: normalizeImportedNumericValue(getFirstValueByAliases(values, ['costofeepatagonia', 'feepatagonia', 'costopatagonia']))
                };
                row.zonasEnvioGratis = {
                    amba: parseBooleanLike(getFirstValueByAliases(values, ['enviogratisamba', 'gratisamba', 'amba'])),
                    interior: parseBooleanLike(getFirstValueByAliases(values, ['enviogratisinterior', 'gratisinterior', 'interiorgratis', 'interior'])),
                    patagonia: parseBooleanLike(getFirstValueByAliases(values, ['enviogratispatagonia', 'gratispatagonia', 'patagoniagratis', 'patagonia']))
                };
                row.dimensiones = {
                    alto: normalizeImportedNumericValue(getFirstValueByAliases(values, ['alto'], row.dimensiones.alto), row.dimensiones.alto),
                    ancho: normalizeImportedNumericValue(getFirstValueByAliases(values, ['ancho'], row.dimensiones.ancho), row.dimensiones.ancho),
                    profundidad: normalizeImportedNumericValue(getFirstValueByAliases(values, ['profundidad', 'prof'], row.dimensiones.profundidad), row.dimensiones.profundidad),
                    peso: normalizeImportedNumericValue(getFirstValueByAliases(values, ['peso'], row.dimensiones.peso), row.dimensiones.peso)
                };
            } else {
                row.skuSeller = values[0] ?? '';
                row.titulo = values[1] ?? '';
                row.ean = values[2] ?? '';
                row.descripcionAmpliada = values[3] ?? '';
                row.precioLista = normalizeImportedNumericValue(values[4] ?? '');
                row.precioPromocional = normalizeImportedNumericValue(values[5] ?? '');
                row.stock = normalizeImportedNumericValue(values[6] ?? '0', '0');
                row.codigoLegibilidad = values[7] ?? '';
                row.linkYoutube = values[8] ?? '';
                row.imagenesTexto = values.slice(9, 14).filter((item) => item !== '').slice(0, 5).join('\n');
                row.costosFee = {
                    amba: normalizeImportedNumericValue(values[14] ?? ''),
                    interior: normalizeImportedNumericValue(values[15] ?? ''),
                    patagonia: normalizeImportedNumericValue(values[16] ?? '')
                };
                row.zonasEnvioGratis = {
                    amba: parseBooleanLike(values[17]),
                    interior: parseBooleanLike(values[18]),
                    patagonia: parseBooleanLike(values[19])
                };
                row.dimensiones = {
                    alto: normalizeImportedNumericValue(values[20] ?? row.dimensiones.alto, row.dimensiones.alto),
                    ancho: normalizeImportedNumericValue(values[21] ?? row.dimensiones.ancho, row.dimensiones.ancho),
                    profundidad: normalizeImportedNumericValue(values[22] ?? row.dimensiones.profundidad, row.dimensiones.profundidad),
                    peso: normalizeImportedNumericValue(values[23] ?? row.dimensiones.peso, row.dimensiones.peso)
                };
            }

            importedRows.push(row);
        });

        if (importedRows.length === 0) {
            toast.warn('No encontramos filas validas para importar.');
            return;
        }

        const importedDefaults = {};
        const importedSellerId = getFirstValueFromRecords(['idseller']);
        const importedCategoria = getFirstValueFromRecords(['idcategoria', 'categoria']);
        const importedMarca = getFirstValueFromRecords(['idmarca', 'marca']);
        const importedTipoPublicacion = getFirstValueFromRecords(['idtipopublicacion', 'tipopublicacion']);
        const importedTipoEntrega = getFirstValueFromRecords(['idtipoentrega', 'tipoentrega']);
        const importedAlicuotaIva = getFirstValueFromRecords(['alicuotaiva', 'idalicuotaiva']);
        const importedImpuestoInterno = getFirstValueFromRecords(['impuestointerno', 'alicuotaimpuestointerno']);
        const importedEnvioGratis = getFirstValueFromRecords(['enviogratis']);
        const importedDescripcion = getFirstValueFromRecords(['descripcionampliada', 'descripcionlarga', 'descripcion']);
        const hasRowDimensions = hasAnyHeader(['alto', 'ancho', 'profundidad', 'prof', 'peso']);

        if (importedCategoria !== '') importedDefaults.categoria = importedCategoria;
        if (importedMarca !== '') importedDefaults.marca = importedMarca;
        if (importedTipoPublicacion !== '') importedDefaults.idTipoPublicacion = importedTipoPublicacion;
        if (importedTipoEntrega !== '') importedDefaults.tipoEntrega = importedTipoEntrega;
        if (importedAlicuotaIva !== '') importedDefaults.alicuotaIva = importedAlicuotaIva;
        if (importedImpuestoInterno !== '') importedDefaults.alicuotaImpuestoInterno = importedImpuestoInterno;
        if (importedEnvioGratis !== '') importedDefaults.envioGratis = parseBooleanLike(importedEnvioGratis);
        if (importedDescripcion !== '' && importedRows.every((row) => row.descripcionAmpliada === importedDescripcion || row.descripcionAmpliada === '')) {
            importedDefaults.descripcionAmpliada = importedDescripcion;
        }
        if (hasRowDimensions) importedDefaults.replicateDimensions = false;

        if (importedSellerId !== '') {
            if (isPrivilegedUser) {
                setSelectedSellerId(importedSellerId);
            } else if (String(currentSellerId ?? '') !== String(importedSellerId)) {
                toast.warn(`El CSV trae seller ${importedSellerId}, pero el wizard usara el seller asociado a tu usuario.`);
            }
        }

        if (Object.keys(importedDefaults).length > 0) {
            setDefaults((prevState) => ({
                ...prevState,
                ...importedDefaults
            }));
        }

        setRows((prevState) => {
            if (prevState.length === 0) {
                return importedRows;
            }

            return [...prevState, ...importedRows];
        });
        setCurrentRowsPage(1);
        setExpandedRows({});
        setPasteValue('');
        setExcelJustLoaded(true);
        window.setTimeout(() => setExcelJustLoaded(false), 700);
        toast.success(`${importedRows.length} fila(s) cargadas desde ${sourceLabel}.`);
    };

    const importFromText = () => {
        const rawText = String(pasteValue ?? '').trim();

        if (rawText === '') {
            toast.info('Pega primero el contenido de tu planilla o CSV.');
            return;
        }

        const records = parseDelimitedText(rawText, detectDelimiter(rawText));
        if (records.length === 0) {
            toast.warn('No encontramos filas validas para importar.');
            return;
        }
        importFromRecords(records, 'texto pegado');
    };

    const importFromExcelFile = async (file) => {
        if (!file) return;
        const fileName = String(file.name ?? '').toLowerCase();
        if (!fileName.endsWith('.csv')) {
            toast.warn(
                'Subí un archivo .csv (podés exportarlo desde Excel como CSV separado por comas o punto y coma).'
            );
            return;
        }
        try {
            const text = await file.text();
            const records = parseDelimitedText(text, detectDelimiter(text));
            if (records.length === 0) {
                toast.warn('No encontramos filas validas para importar.');
                return;
            }
            importFromRecords(records, 'CSV');
        } catch (error) {
            toast.error('No se pudo leer el archivo CSV.');
        }
    };

    const onExcelInputChange = (event) => {
        const file = event?.target?.files?.[0];
        importFromExcelFile(file);
        if (event?.target) event.target.value = '';
    };

    const onExcelDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setExcelDragDepth(0);
        const file = event.dataTransfer?.files?.[0];
        importFromExcelFile(file);
    };

    const onExcelDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setExcelDragDepth((depth) => depth + 1);
    };

    const onExcelDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setExcelDragDepth((depth) => Math.max(depth - 1, 0));
    };

    const buildRowImages = async (row) => {
        const manualImages = parseImageUrls(row.imagenesTexto).slice(0, 5).map((urlImagen, index) => ({
            posicion: index + 1,
            urlImagen
        }));

        const uploadedImages = await Promise.all(
            (row.imageFiles ?? []).slice(0, Math.max(0, 5 - manualImages.length)).map(async (file, index) => {
                const uploadResponse = await createImageUrl(file);
                return {
                    posicion: manualImages.length + index + 1,
                    urlImagen: uploadResponse?.url ?? ''
                };
            })
        );

        return [...manualImages, ...uploadedImages].filter((image) => image.urlImagen);
    };

    const buildRequest = async () => {
        const masivaBulks = await Promise.all(rows.map(async (row) => ({
                idSeller: toNumber(selectedSellerId, 0),
                observacion: defaults.observacion,
                titulo: row.titulo.trim(),
                ean: row.ean.trim(),
                precioLista: toNumber(row.precioLista, 0),
                precioPromocional: row.precioPromocional === '' ? 0 : toNumber(row.precioPromocional, 0),
                linkYoutube: row.linkYoutube.trim(),
                descripcionAmpliada: (row.descripcionAmpliada || defaults.descripcionAmpliada).trim(),
                tipoEntrega: toNumber(defaults.tipoEntrega, 1),
                envioGratis: !!(defaults.envioPropio
                    ? Object.values(row.zonasEnvioGratis ?? {}).some(Boolean)
                    : defaults.envioGratis),
                dimensiones: [
                    {
                        alto: toNumber(defaults.replicateDimensions ? defaults.alto : row.dimensiones?.alto, 1),
                        ancho: toNumber(defaults.replicateDimensions ? defaults.ancho : row.dimensiones?.ancho, 1),
                        profundidad: toNumber(defaults.replicateDimensions ? defaults.profundidad : row.dimensiones?.profundidad, 1),
                        peso: toNumber(defaults.replicateDimensions ? defaults.peso : row.dimensiones?.peso, 1)
                    }
                ],
                categoria: toNumber(defaults.categoria, 0),
                marca: toNumber(defaults.marca, 0),
                skuSeller: row.skuSeller.trim(),
                stock: toNumber(row.stock, 0),
                imagenes: await buildRowImages(row),
                idTipoPublicacion: toNumber(defaults.idTipoPublicacion, 0),
                envioPropio: !!defaults.envioPropio,
                garantiaExtActiva: !!defaults.garantiaExtActiva,
                garantiaFabrica: toNumber(defaults.garantiaFabrica, 0),
                codigoLegibilidad: row.codigoLegibilidad.trim(),
                idMoneda: 0,
                alicuotaIva: toNumber(defaults.alicuotaIva, 0),
                alicuotaImpuestoInterno: toNumber(defaults.alicuotaImpuestoInterno, 0),
                costoFeeAmba: toNumber(row.costosFee?.amba, 0),
                costoFeeInterior: toNumber(row.costosFee?.interior, 0),
                costoFeePatagonia: toNumber(row.costosFee?.patagonia, 0),
                envioGratisZona: {
                    amba: !!(defaults.envioPropio ? row.zonasEnvioGratis?.amba : defaults.envioGratis && row.zonasEnvioGratis?.amba),
                    interior: !!(defaults.envioPropio ? row.zonasEnvioGratis?.interior : defaults.envioGratis && row.zonasEnvioGratis?.interior),
                    patagonia: !!(defaults.envioPropio ? row.zonasEnvioGratis?.patagonia : defaults.envioGratis && row.zonasEnvioGratis?.patagonia)
                }
            })));

        return {
            masivaBulks,
            idUser: userId
        };
    };

    const handleSubmit = async () => {
        if (!prepareReview()) {
            setStep(1);
            return;
        }

        setIsSubmitting(true);

        try {
            const rowsSnapshot = cloneRowsSnapshot(rows);
            const request = await buildRequest();
            const response = await createPublicacionApi(request);
            const created = Array.isArray(response?.publicacion)
                ? response.publicacion
                : Array.isArray(response?.publicaciones)
                    ? response.publicaciones
                : Array.isArray(response?.data?.publicacion)
                    ? response.data.publicacion
                    : Array.isArray(response?.data?.publicaciones)
                        ? response.data.publicaciones
                    : [];
            const errors = Array.isArray(response?.errors)
                ? response.errors
                : Array.isArray(response?.errores)
                    ? response.errores
                : Array.isArray(response?.data?.errors)
                    ? response.data.errors
                    : Array.isArray(response?.data?.errores)
                        ? response.data.errores
                    : [];
            const normalizedCreated = created.map((item) => normalizeApiPayload(item));
            const normalizedErrors = enrichApiErrorsWithRowContext(errors, rowsSnapshot, normalizedCreated);

            setResult({ created: normalizedCreated, errors: normalizedErrors });
            setStep(3);
            toastWizardCreationSummary(normalizedCreated, normalizedErrors);
        } catch (error) {
            const rowsSnapshot = cloneRowsSnapshot(rows);
            const apiErrors = Array.isArray(error?.response?.data?.errores)
                ? error.response.data.errores
                : Array.isArray(error?.response?.data?.errors)
                    ? error.response.data.errors
                    : [];

            const enriched = enrichApiErrorsWithRowContext(apiErrors, rowsSnapshot);

            setResult({
                created: [],
                errors: enriched
            });
            setStep(3);
            if (enriched.length > 0) {
                toastWizardCreationSummary([], enriched);
            } else {
                toast.error(error?.message || 'No se pudieron crear las publicaciones.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetWizard = () => {
        setStep(0);
        setDefaults(createInitialDefaults(isPrivilegedUser ? '' : currentSellerId));
        setSelectedSellerId(isPrivilegedUser ? '' : String(currentSellerId ?? ''));
        setSellerSearch('');
        setRows([]);
        setCurrentRowsPage(1);
        setExpandedRows({});
        setPasteValue('');
        setContextErrors({});
        setResult({ created: [], errors: [] });
        setExcelDragDepth(0);
        setExcelJustLoaded(false);
    };

    const safeRows = Array.isArray(rows) ? rows : [];
    const excelDragging = excelDragDepth > 0;
    const csvRowsCount = safeRows.filter((row) => row?.source === 'csv').length;
    const safeSellerOptions = Array.isArray(sellerOptions) ? sellerOptions : [];
    const safeCategorias = Array.isArray(categorias) ? categorias : [];
    const safeMarcas = Array.isArray(marcas) ? marcas : [];
    const safeTiposPublicacion = Array.isArray(tiposPublicacion) ? tiposPublicacion : [];
    const createdResults = Array.isArray(result?.created) ? result.created : [];
    const errorResults = Array.isArray(result?.errors) ? result.errors : [];
    const liveRowErrors = getRowErrors(safeRows, defaults);
    const rowsWithIssuesCount = liveRowErrors.filter((rowError) => Object.keys(rowError).length > 0).length;
    const totalPages = Math.max(1, Math.ceil(safeRows.length / ROWS_PER_PAGE));
    const visibleRows = safeRows.slice((currentRowsPage - 1) * ROWS_PER_PAGE, currentRowsPage * ROWS_PER_PAGE);
    const summary = getSummary(safeRows, defaults);
    const selectedSellerLabel = safeSellerOptions.find((seller) => String(seller.value) === String(selectedSellerId))?.label;
    const selectedCategoriaLabel = safeCategorias.find((item) => String(item.value) === String(defaults.categoria))?.label ?? String(defaults.categoria || '-');
    const selectedMarcaLabel = safeMarcas.find((item) => String(item.value) === String(defaults.marca))?.label ?? String(defaults.marca || '-');
    const selectedTipo = safeTiposPublicacion.find((item) => String(item?.codigo ?? item?.Codigo) === String(defaults.idTipoPublicacion));
    const selectedTipoLabel = selectedTipo?.descripcion ?? selectedTipo?.Descripcion ?? String(defaults.idTipoPublicacion || '-');

    const handleNext = () => {
        if (step === 0) {
            const errors = validateContextStep();
            if (Object.keys(errors).length > 0) {
                toast.warn('Completa el contexto del lote antes de continuar.');
                return;
            }
            setStep(1);
            return;
        }

        if (step === 1) {
            if (!prepareReview()) return;
            setStep(2);
        }
    };

    const handleBack = () => {
        if (step === 0) return;
        setStep((currentStep) => currentStep - 1);
    };

    const downloadCreationTemplate = useCallback(async () => {
        try {
            const { data } = await getFormatoExcel({ tipoExcel: 4 });
            let b64 = data?.excel ?? data?.archivoBase64 ?? data?.base64 ?? (typeof data === 'string' ? data : '');
            if (!b64) throw new Error('Respuesta sin base64');
            const comma = b64.indexOf(',');
            if (comma !== -1) b64 = b64.slice(comma + 1);
            b64 = b64.replace(/-/g, '+').replace(/_/g, '/');
            while (b64.length % 4 !== 0) b64 += '=';
            const blob = base64ToBlob(b64, 'text/csv;charset=utf-8;');
            const fileName = CREACION_MASIVA_TEMPLATE_NAME.endsWith('.csv')
                ? CREACION_MASIVA_TEMPLATE_NAME
                : `${CREACION_MASIVA_TEMPLATE_NAME}.csv`;
            saveByteArrayXlsx(fileName, blob);
        } catch (e) {
            console.error(e);
            toast.error('No se pudo descargar el formato CSV.');
        }
    }, []);

    if (tipoUsuario === 6) {
        return null;
    }

    return (
        <div className="container wizard-publicaciones-page">
            <div className="wizard-publicaciones-shell">
                <div className="wizard-publicaciones-hero">
                    <div>
                        <span className="wizard-publicaciones-kicker">Creación masiva</span>
                        <h2 className="wizard-publicaciones-title">Wizard guiado para crear publicaciones por lote</h2>
                        <p className="wizard-publicaciones-description">
                            Cargá un lote de publicaciones paso a paso: plantilla, datos del lote y confirmación.
                        </p>
                    </div>

                    <div className="wizard-publicaciones-hero-actions">
                        <button
                            type="button"
                            className="btn btn-sm btn-green"
                            onClick={downloadCreationTemplate}
                        >
                            Descargar formato de ejemplo
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => navigate('/Publicaciones/CreacionMasivaExcel')}
                        >
                            Carga por CSV (legacy)
                        </button>
                        <button
                            type="button"
                            className="btn btn-light wizard-publicaciones-primary-light"
                            onClick={() => navigate('/Publicaciones')}
                        >
                            Ir a publicaciones
                        </button>
                    </div>
                </div>

                <div className="wizard-publicaciones-stepper">
                    {STEP_LABELS.map((label, index) => {
                        const stateClass = index === step
                            ? 'is-current'
                            : index < step
                                ? 'is-complete'
                                : '';

                        return (
                            <div key={label} className={`wizard-publicaciones-step ${stateClass}`}>
                                <div className="wizard-publicaciones-step-node">{index + 1}</div>
                                <div className="wizard-publicaciones-step-label">{label}</div>
                            </div>
                        );
                    })}
                </div>

                <div className="wizard-publicaciones-card">
                    {step === 0 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>1. Datos lote actualizacion</h3>
                                    <p>Defini los datos comunes que van a compartir las publicaciones del lote.</p>
                                </div>

                                <div className="wizard-publicaciones-tip">
                                    <span>Descargá el formato de ejemplo antes de armar el lote.</span>
                                    <button
                                        type="button"
                                        className="btn btn-link btn-sm p-0 align-baseline ms-1"
                                        onClick={downloadCreationTemplate}
                                    >
                                        Descargar formato
                                    </button>
                                </div>
                            </div>

                            <div className="wizard-creacion-dropzone-wrap mb-3">
                                <label className="form-label">Arrastrar archivo CSV</label>
                                <div
                                    className={`wizard-creacion-dropzone ${excelDragging ? 'is-dragging' : ''} ${
                                        excelJustLoaded ? 'is-loaded-flash' : ''
                                    }`}
                                    onDragEnter={onExcelDragEnter}
                                    onDragLeave={onExcelDragLeave}
                                    onDragOver={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onDrop={onExcelDrop}
                                    role="presentation"
                                >
                                    <input
                                        ref={excelInputRef}
                                        type="file"
                                        accept=".csv,text/csv"
                                        className="wizard-creacion-dropzone-input"
                                        onChange={onExcelInputChange}
                                    />
                                    <p className="wizard-creacion-dropzone-title mb-1">
                                        Arrastrá y soltá un CSV aquí
                                    </p>
                                    <p className="wizard-creacion-dropzone-sub mb-0">
                                        O{' '}
                                        <button
                                            type="button"
                                            className="btn btn-link btn-sm p-0 align-baseline"
                                            onClick={() => excelInputRef.current?.click()}
                                        >
                                            elegi un archivo
                                        </button>
                                        . Se importa directo a la actualizacion.
                                    </p>
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel">
                                        <h4>Seller y catalogo</h4>
                                        {isPrivilegedUser ? (
                                            <>
                                                <label className="form-label">Buscar seller</label>
                                                <div className="wizard-publicaciones-inline-search">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={sellerSearch}
                                                        onChange={(event) => setSellerSearch(event.target.value)}
                                                        placeholder="Razon social o codigo"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => loadSellers(sellerSearch)}
                                                        disabled={isLoadingSellers}
                                                    >
                                                        {isLoadingSellers ? 'Buscando...' : 'Buscar'}
                                                    </button>
                                                </div>
                                                <label className="form-label mt-3">Seller del lote</label>
                                                <select
                                                    className={`form-select ${contextErrors.idSeller ? 'is-invalid' : ''}`}
                                                    value={selectedSellerId}
                                                    onChange={(event) => setSelectedSellerId(event.target.value)}
                                                >
                                                    <option value="">Selecciona un seller</option>
                                                    {sellerOptions.map((seller) => (
                                                        <option key={seller.value} value={seller.value}>
                                                            {seller.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {contextErrors.idSeller && (
                                                    <div className="invalid-feedback d-block">{contextErrors.idSeller}</div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="wizard-publicaciones-fixed-value">
                                                <span className="wizard-publicaciones-fixed-label">Seller asociado</span>
                                                <strong>{selectedSellerId || 'Sin seller asignado'}</strong>
                                                {contextErrors.idSeller && (
                                                    <div className="text-danger small mt-2">{contextErrors.idSeller}</div>
                                                )}
                                            </div>
                                        )}

                                        <div className="row g-3 mt-1">
                                            <div className="col-12">
                                                <label className="form-label">Buscar categoria</label>
                                                <div className="wizard-publicaciones-inline-search">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={categoriaSearch}
                                                        onChange={(event) => setCategoriaSearch(event.target.value)}
                                                        placeholder="Subfamilia o categoria"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => dispatch(fetchCategorias(categoriaSearch))}
                                                    >
                                                        Buscar
                                                    </button>
                                                </div>
                                                <select
                                                    className={`form-select mt-2 ${contextErrors.categoria ? 'is-invalid' : ''}`}
                                                    name="categoria"
                                                    value={defaults.categoria}
                                                    onChange={handleDefaultsChange}
                                                >
                                                    <option value="">Selecciona una categoria</option>
                                                    {safeCategorias.map((categoria) => (
                                                        <option key={categoria.value} value={categoria.value}>
                                                            {categoria.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {contextErrors.categoria && (
                                                    <div className="invalid-feedback d-block">{contextErrors.categoria}</div>
                                                )}
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label">Buscar marca</label>
                                                <div className="wizard-publicaciones-inline-search">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={marcaSearch}
                                                        onChange={(event) => setMarcaSearch(event.target.value)}
                                                        placeholder="Marca"
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary"
                                                        onClick={() => dispatch(fetchMarcas(marcaSearch))}
                                                    >
                                                        Buscar
                                                    </button>
                                                </div>
                                                <select
                                                    className={`form-select mt-2 ${contextErrors.marca ? 'is-invalid' : ''}`}
                                                    name="marca"
                                                    value={defaults.marca}
                                                    onChange={handleDefaultsChange}
                                                >
                                                    <option value="">Selecciona una marca</option>
                                                    {safeMarcas.map((marca) => (
                                                        <option key={marca.value} value={marca.value}>
                                                            {marca.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                {contextErrors.marca && (
                                                    <div className="invalid-feedback d-block">{contextErrors.marca}</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel">
                                        <h4>Configuracion comun</h4>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <label className="form-label">Tipo de publicacion</label>
                                                <select
                                                    className={`form-select ${contextErrors.idTipoPublicacion ? 'is-invalid' : ''}`}
                                                    name="idTipoPublicacion"
                                                    value={defaults.idTipoPublicacion}
                                                    onChange={handleDefaultsChange}
                                                >
                                                    <option value="">Selecciona un tipo</option>
                                                    {safeTiposPublicacion.map((tipo) => {
                                                        const value = tipo?.codigo ?? tipo?.Codigo ?? tipo?.idTipoPublicacion ?? 0;
                                                        const label = tipo?.descripcion ?? tipo?.Descripcion ?? '';
                                                        return (
                                                            <option key={value} value={value}>
                                                                {label}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                                {contextErrors.idTipoPublicacion && (
                                                    <div className="invalid-feedback d-block">{contextErrors.idTipoPublicacion}</div>
                                                )}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Alicuota IVA</label>
                                                <select
                                                    className={`form-select ${contextErrors.alicuotaIva ? 'is-invalid' : ''}`}
                                                    name="alicuotaIva"
                                                    value={defaults.alicuotaIva}
                                                    onChange={handleDefaultsChange}
                                                >
                                                    <option value="">Selecciona IVA</option>
                                                    {(Array.isArray(tiposAlicuotaIva) ? tiposAlicuotaIva : []).map((iva) => (
                                                        <option key={iva.alicuota} value={iva.alicuota}>
                                                            {iva.alicuota}%
                                                        </option>
                                                    ))}
                                                </select>
                                                {contextErrors.alicuotaIva && (
                                                    <div className="invalid-feedback d-block">{contextErrors.alicuotaIva}</div>
                                                )}
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Impuesto interno</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="alicuotaImpuestoInterno"
                                                    value={defaults.alicuotaImpuestoInterno}
                                                    onChange={handleDefaultsChange}
                                                    min="0"
                                                    step="0.01"
                                                />
                                            </div>

                                            <div className="col-md-6">
                                                <label className="form-label">Tipo de entrega</label>
                                                <select
                                                    className="form-select"
                                                    name="tipoEntrega"
                                                    value={defaults.tipoEntrega}
                                                    onChange={handleDefaultsChange}
                                                >
                                                    <option value="1">Envio a domicilio / sucursal</option>
                                                    <option value="2">Retiro en sucursal</option>
                                                    <option value="3">Envio propio</option>
                                                </select>
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label">Observacion comun</label>
                                                <textarea
                                                    className="form-control"
                                                    name="observacion"
                                                    value={defaults.observacion}
                                                    onChange={handleDefaultsChange}
                                                    rows="2"
                                                    placeholder="Se replica en todas las publicaciones del lote"
                                                />
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label">Descripcion ampliada comun</label>
                                                <textarea
                                                    className="form-control"
                                                    name="descripcionAmpliada"
                                                    value={defaults.descripcionAmpliada}
                                                    onChange={handleDefaultsChange}
                                                    rows="4"
                                                    placeholder="Descripcion general para el lote"
                                                />
                                            </div>

                                            <div className="col-12">
                                                <div className="wizard-publicaciones-switches">
                                                    <label className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="envioGratis"
                                                            checked={defaults.envioGratis}
                                                            onChange={handleDefaultsChange}
                                                        />
                                                        <span className="form-check-label">Envio gratis</span>
                                                    </label>
                                                    <label className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="envioPropio"
                                                            checked={defaults.envioPropio}
                                                            onChange={handleDefaultsChange}
                                                        />
                                                        <span className="form-check-label">Envio propio</span>
                                                    </label>
                                                    <label className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="garantiaExtActiva"
                                                            checked={defaults.garantiaExtActiva}
                                                            onChange={handleDefaultsChange}
                                                        />
                                                        <span className="form-check-label">Garantia extendida</span>
                                                    </label>
                                                    <label className="form-check">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            name="replicateDimensions"
                                                            checked={defaults.replicateDimensions}
                                                            onChange={handleDefaultsChange}
                                                        />
                                                        <span className="form-check-label">Replicar dimensiones a todo el lote</span>
                                                    </label>
                                                </div>
                                            </div>

                                            {defaults.envioPropio && (
                                                <div className="col-12">
                                                    <div className="wizard-publicaciones-note">
                                                        El wizard permite definir envio gratis y costo fee por zona para cada publicacion.
                                                        
                                                    </div>
                                                </div>
                                            )}

                                            <div className="col-md-6">
                                                <label className="form-label">Meses de garantia fabrica</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    name="garantiaFabrica"
                                                    value={defaults.garantiaFabrica}
                                                    onChange={handleDefaultsChange}
                                                    min="0"
                                                />
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label">
                                                    {defaults.replicateDimensions ? 'Dimensiones base' : 'Dimensiones por fila'}
                                                </label>
                                                {defaults.replicateDimensions ? (
                                                    <div className="row g-2">
                                                        <div className="col-6 col-md-3">
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="alto"
                                                                value={defaults.alto}
                                                                onChange={handleDefaultsChange}
                                                                min="0"
                                                                step="0.01"
                                                                placeholder="Alto"
                                                            />
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="ancho"
                                                                value={defaults.ancho}
                                                                onChange={handleDefaultsChange}
                                                                min="0"
                                                                step="0.01"
                                                                placeholder="Ancho"
                                                            />
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="profundidad"
                                                                value={defaults.profundidad}
                                                                onChange={handleDefaultsChange}
                                                                min="0"
                                                                step="0.01"
                                                                placeholder="Prof."
                                                            />
                                                        </div>
                                                        <div className="col-6 col-md-3">
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                name="peso"
                                                                value={defaults.peso}
                                                                onChange={handleDefaultsChange}
                                                                min="0"
                                                                step="0.01"
                                                                placeholder="Peso"
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="wizard-publicaciones-note wizard-publicaciones-note--neutral">
                                                        Las dimensiones se completan individualmente en cada fila del lote.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>2. Carga base del lote</h3>
                                    <p>
                                        Puedes pegar filas desde Excel o editar la grilla manualmente.
                                        Orden esperado: IdSeller,Titulo,EAN,PrecioLista,PrecioPromocional,DescripcionAmpliada,
                                        EnvioGratis,IdTipoEntrega,IdCategoria,IdMarca,SKUSeller,Stock,IdTipoPublicacion,Alto,
                                        Ancho,Profundidad,Peso,Img1,Img2,Img3,Img4,Img5,ImpuestoInterno,AlicuotaIva,EnvioGratis,
                                        Amba,Interior,Patagonia
.
                                    </p>
                                </div>
                                <div className="wizard-publicaciones-tip">
                                    Validamos SKU, titulo, precio y stock antes de ejecutar el alta.
                                </div>
                            </div>

                            <div className="wizard-publicaciones-pastebox">
                                <label className="form-label">Pegar filas desde Excel o CSV</label>
                                <textarea
                                    className="form-control"
                                    rows="5"
                                    value={pasteValue}
                                    onChange={(event) => setPasteValue(event.target.value)}
                                    placeholder={'Pegar aqui contenido csv...'}
                                />
                                <div className="wizard-publicaciones-pastebox-actions">
                                    <button type="button" className="btn btn-outline-secondary" onClick={importFromText}>
                                        Cargar filas desde texto
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger"
                                        onClick={removeCsvRows}
                                        disabled={csvRowsCount === 0}
                                    >
                                        Eliminar filas del CSV
                                    </button>
                                    <button type="button" className="btn btn-light" onClick={addEmptyRow}>
                                        Agregar fila manual
                                    </button>
                                    <button type="button" className="btn btn-outline-secondary" onClick={clearRowsLote}>
                                        Vaciar lote
                                    </button>
                                </div>
                                <div className="wizard-publicaciones-note wizard-publicaciones-note--neutral mt-3 mb-0">
                                    Puedes pegar CSV reales con `;`, comillas y saltos de linea dentro de `DescripcionAmpliada`.
                                    Tambien tomamos columnas como `IdSeller`, `IdCategoria`, `IdMarca`, `IdTipoPublicacion`, `IdTipoEntrega`, `AlicuotaIva`, `ImpuestoInterno`, `Img1` a `Img5`, `Amba`, `Interior`, `Patagonia`, `alto`, `ancho`, `profundidad` y `peso`.
                                </div>
                            </div>

                            <div className="wizard-publicaciones-table-toolbar">
                                <div className="wizard-publicaciones-table-meta">
                                    <strong>{safeRows.length}</strong> fila(s) en el lote
                                    <span>{csvRowsCount} importada(s) desde CSV</span>
                                    <span>{rowsWithIssuesCount} con datos pendientes</span>
                                    {safeRows.length > 0 && <span>Pagina {currentRowsPage} de {totalPages}</span>}
                                </div>
                                <div className="wizard-publicaciones-table-pagination">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setCurrentRowsPage((page) => Math.max(1, page - 1))}
                                        disabled={currentRowsPage === 1}
                                    >
                                        Anterior
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setCurrentRowsPage((page) => Math.min(totalPages, page + 1))}
                                        disabled={currentRowsPage === totalPages}
                                    >
                                        Siguiente
                                    </button>
                                </div>
                            </div>

                            {safeRows.length === 0 ? (
                                <div className="wizard-publicaciones-empty-lote">
                                    <h4>El lote esta vacio</h4>
                                    <p>
                                        Pega un CSV, importa filas desde texto o agrega una fila manual solo si la necesitas.
                                        
                                    </p>
                                    <div className="wizard-publicaciones-empty-lote-actions">
                                        <button type="button" className="btn btn-light" onClick={addEmptyRow}>
                                            Agregar fila manual
                                        </button>
                                    </div>
                                </div>
                            ) : (
                            <div className="wizard-publicaciones-table-wrapper">
                                <table className="table table-hover align-middle wizard-publicaciones-table">
                                    <thead>
                                        <tr>
                                            <th>Fila</th>
                                            <th className="wizard-publicaciones-col-status">Estado</th>
                                            <th>SKU seller</th>
                                            <th>Titulo</th>
                                            <th>EAN</th>
                                            <th>Precio lista</th>
                                            <th>Precio promo</th>
                                            <th>Stock</th>
                                            <th>Cod. legibilidad</th>
                                            <th>Link video</th>
                                            <th>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleRows.map((row, index) => {
                                            const rowIndex = ((currentRowsPage - 1) * ROWS_PER_PAGE) + index;
                                            const rowErrors = liveRowErrors[rowIndex] ?? {};
                                            const rowIssues = getRowIssueDetails(rowErrors);
                                            const issuePreview = rowIssues.slice(0, 4);
                                            const remainingIssues = Math.max(0, rowIssues.length - issuePreview.length);
                                            const rowIsBlank = isRowBlank(row);
                                            const isExpanded = !!expandedRows[row.id];
                                            const imageCount = getRowImageCount(row);
                                            const statusClass = rowIsBlank
                                                ? 'is-empty'
                                                : rowIssues.length > 0
                                                    ? 'is-invalid'
                                                    : 'is-valid';

                                            return (
                                            <React.Fragment key={row.id}>
                                            <tr>
                                                <td>
                                                    <div className="wizard-publicaciones-row-index">
                                                        <strong>{rowIndex + 1}</strong>
                                                        <span>{row.source === 'csv' ? 'CSV' : 'Manual'}</span>
                                                    </div>
                                                </td>
                                                <td className="wizard-publicaciones-cell-status">
                                                    <div className={`wizard-publicaciones-row-status ${statusClass}`}>
                                                        <strong>
                                                            {rowIsBlank
                                                                ? 'Fila vacia'
                                                                : rowIssues.length > 0
                                                                    ? `${rowIssues.length} dato(s) a revisar`
                                                                    : 'Lista para crear'}
                                                        </strong>
                                                        <span>
                                                            {rowIsBlank
                                                                ? 'Completa los campos obligatorios o elimina la fila.'
                                                                : rowIssues.length > 0
                                                                    ? 'Campos faltantes o invalidos:'
                                                                    : 'La fila tiene SKU, titulo, precio, stock e imagenes.'}
                                                        </span>
                                                        {rowIssues.length > 0 && (
                                                            <div className="wizard-publicaciones-row-issue-list">
                                                                {issuePreview.map((issue) => (
                                                                    <span
                                                                        key={`${row.id}-${issue.field}`}
                                                                        className="wizard-publicaciones-row-issue"
                                                                        title={issue.message}
                                                                    >
                                                                        {issue.label}
                                                                    </span>
                                                                ))}
                                                                {remainingIssues > 0 && (
                                                                    <span className="wizard-publicaciones-row-issue-more">+{remainingIssues} mas</span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${rowErrors?.skuSeller ? 'is-invalid' : ''}`}
                                                        value={row.skuSeller}
                                                        onChange={(event) => handleRowChange(row.id, 'skuSeller', event.target.value)}
                                                        placeholder={`SKU-${rowIndex + 1}`}
                                                    />
                                                    {rowErrors?.skuSeller && (
                                                        <div className="invalid-feedback d-block">{rowErrors.skuSeller}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className={`form-control ${rowErrors?.titulo ? 'is-invalid' : ''}`}
                                                        value={row.titulo}
                                                        onChange={(event) => handleRowChange(row.id, 'titulo', event.target.value)}
                                                        placeholder="Titulo de venta"
                                                    />
                                                    {rowErrors?.titulo && (
                                                        <div className="invalid-feedback d-block">{rowErrors.titulo}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={row.ean}
                                                        onChange={(event) => handleRowChange(row.id, 'ean', event.target.value)}
                                                        placeholder="EAN"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${rowErrors?.precioLista ? 'is-invalid' : ''}`}
                                                        value={row.precioLista}
                                                        onChange={(event) => handleRowChange(row.id, 'precioLista', event.target.value)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {rowErrors?.precioLista && (
                                                        <div className="invalid-feedback d-block">{rowErrors.precioLista}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${rowErrors?.precioPromocional ? 'is-invalid' : ''}`}
                                                        value={row.precioPromocional}
                                                        onChange={(event) => handleRowChange(row.id, 'precioPromocional', event.target.value)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                    {rowErrors?.precioPromocional && (
                                                        <div className="invalid-feedback d-block">{rowErrors.precioPromocional}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        className={`form-control ${rowErrors?.stock ? 'is-invalid' : ''}`}
                                                        value={row.stock}
                                                        onChange={(event) => handleRowChange(row.id, 'stock', event.target.value)}
                                                        min="0"
                                                    />
                                                    {rowErrors?.stock && (
                                                        <div className="invalid-feedback d-block">{rowErrors.stock}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={row.codigoLegibilidad}
                                                        onChange={(event) => handleRowChange(row.id, 'codigoLegibilidad', event.target.value)}
                                                        placeholder="Opcional"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={row.linkYoutube}
                                                        onChange={(event) => handleRowChange(row.id, 'linkYoutube', event.target.value)}
                                                        placeholder="Opcional"
                                                    />
                                                </td>
                                                <td>
                                                    <div className="wizard-publicaciones-table-actions">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => duplicateRow(row.id)}
                                                        >
                                                            Duplicar
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => removeRow(row.id)}
                                                        >
                                                            Quitar
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-dark"
                                                            onClick={() => toggleRowExpanded(row.id)}
                                                        >
                                                            {isExpanded ? 'Ocultar detalle' : 'Ver detalle y editar'}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                            <tr className="wizard-publicaciones-details-row">
                                                <td colSpan="11">
                                                    <div className="wizard-publicaciones-row-details">
                                                        <div className="wizard-publicaciones-row-detail-header">
                                                            <div>
                                                                <span className="wizard-publicaciones-row-detail-eyebrow">
                                                                    Fila {rowIndex + 1} | {row.source === 'csv' ? 'Importada desde CSV' : 'Carga manual'}
                                                                </span>
                                                                <strong>{row.titulo || row.skuSeller || 'Completa los datos de esta publicacion'}</strong>
                                                            </div>
                                                            <span className={`wizard-publicaciones-row-detail-badge ${statusClass}`}>
                                                                {rowIsBlank ? 'Fila vacia' : rowIssues.length > 0 ? 'Con pendientes' : 'Lista para crear'}
                                                            </span>
                                                        </div>

                                                        {rowIssues.length > 0 && (
                                                            <div className="wizard-publicaciones-row-detail-alert">
                                                                <strong>Campos a revisar</strong>
                                                                <div className="wizard-publicaciones-row-issue-list">
                                                                    {rowIssues.map((issue) => (
                                                                        <span
                                                                            key={`${row.id}-detail-${issue.field}`}
                                                                            className="wizard-publicaciones-row-issue"
                                                                        >
                                                                            {issue.label}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                                <div className="wizard-publicaciones-row-detail-messages">
                                                                    {rowIssues.map((issue) => (
                                                                        <span key={`${row.id}-message-${issue.field}`}>
                                                                            {issue.label}: {issue.message}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                        <div className="wizard-publicaciones-row-detail-card">
                                                            <h5>Descripcion ampliada</h5>
                                                            <textarea
                                                                className="form-control"
                                                                rows="8"
                                                                value={row.descripcionAmpliada}
                                                                onChange={(event) => handleRowChange(row.id, 'descripcionAmpliada', event.target.value)}
                                                                placeholder="Si queda vacia, se usa la descripcion comun del lote"
                                                            />
                                                        </div>

                                                        <div className="wizard-publicaciones-row-detail-card">
                                                            <h5>Imagenes de la publicacion</h5>
                                                            <div className="wizard-publicaciones-row-detail-meta">
                                                                <span>{parseImageUrls(row.imagenesTexto).length} URL(s)</span>
                                                                <span>{row.imageFiles?.length ?? 0} archivo(s)</span>
                                                                <span>{imageCount} / 5 imagenes</span>
                                                            </div>
                                                            <label className="form-label">URLs de imagen</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="5"
                                                                value={row.imagenesTexto}
                                                                onChange={(event) => handleRowChange(row.id, 'imagenesTexto', event.target.value)}
                                                                placeholder={'Una URL por linea\nhttps://cdn.ejemplo.com/imagen-1.jpg'}
                                                            />
                                                            <div className="wizard-publicaciones-upload-row">
                                                                <input
                                                                    type="file"
                                                                    accept="image/*"
                                                                    multiple
                                                                    className="form-control"
                                                                    onChange={(event) => handleRowFilesChange(row.id, event.target.files)}
                                                                />
                                                            </div>
                                                            <div className="wizard-publicaciones-chip-list">
                                                                {parseImageUrls(row.imagenesTexto).map((_, imageIndex) => (
                                                                    <span key={`${row.id}-url-${imageIndex}`} className="wizard-publicaciones-chip">
                                                                        URL {imageIndex + 1}
                                                                    </span>
                                                                ))}
                                                                {(row.imageFiles ?? []).map((file, fileIndex) => (
                                                                    <button
                                                                        type="button"
                                                                        key={`${row.id}-file-${fileIndex}`}
                                                                        className="wizard-publicaciones-chip wizard-publicaciones-chip--button"
                                                                        onClick={() => removeRowFile(row.id, fileIndex)}
                                                                    >
                                                                        {file.name} x
                                                                    </button>
                                                                ))}
                                                            </div>
                                                            {rowErrors?.imagenes && (
                                                                <div className="invalid-feedback d-block mt-2">{rowErrors.imagenes}</div>
                                                            )}
                                                        </div>

                                                        <div className="wizard-publicaciones-row-detail-card">
                                                            <h5>{defaults.replicateDimensions ? 'Dimensiones replicadas' : 'Dimensiones de la fila'}</h5>
                                                            {defaults.replicateDimensions ? (
                                                                <p className="wizard-publicaciones-empty mb-0">
                                                                    Esta fila usara las dimensiones definidas en el contexto del lote.
                                                                </p>
                                                            ) : (
                                                                <div className="row g-2">
                                                                    <div className="col-6">
                                                                        <input
                                                                            type="number"
                                                                            className={`form-control ${rowErrors?.alto ? 'is-invalid' : ''}`}
                                                                            value={row.dimensiones?.alto ?? ''}
                                                                            onChange={(event) => handleRowDimensionChange(row.id, 'alto', event.target.value)}
                                                                            min="0"
                                                                            step="0.01"
                                                                            placeholder="Alto"
                                                                        />
                                                                        {rowErrors?.alto && <div className="invalid-feedback d-block">{rowErrors.alto}</div>}
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <input
                                                                            type="number"
                                                                            className={`form-control ${rowErrors?.ancho ? 'is-invalid' : ''}`}
                                                                            value={row.dimensiones?.ancho ?? ''}
                                                                            onChange={(event) => handleRowDimensionChange(row.id, 'ancho', event.target.value)}
                                                                            min="0"
                                                                            step="0.01"
                                                                            placeholder="Ancho"
                                                                        />
                                                                        {rowErrors?.ancho && <div className="invalid-feedback d-block">{rowErrors.ancho}</div>}
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <input
                                                                            type="number"
                                                                            className={`form-control ${rowErrors?.profundidad ? 'is-invalid' : ''}`}
                                                                            value={row.dimensiones?.profundidad ?? ''}
                                                                            onChange={(event) => handleRowDimensionChange(row.id, 'profundidad', event.target.value)}
                                                                            min="0"
                                                                            step="0.01"
                                                                            placeholder="Profundidad"
                                                                        />
                                                                        {rowErrors?.profundidad && <div className="invalid-feedback d-block">{rowErrors.profundidad}</div>}
                                                                    </div>
                                                                    <div className="col-6">
                                                                        <input
                                                                            type="number"
                                                                            className={`form-control ${rowErrors?.peso ? 'is-invalid' : ''}`}
                                                                            value={row.dimensiones?.peso ?? ''}
                                                                            onChange={(event) => handleRowDimensionChange(row.id, 'peso', event.target.value)}
                                                                            min="0"
                                                                            step="0.01"
                                                                            placeholder="Peso"
                                                                        />
                                                                        {rowErrors?.peso && <div className="invalid-feedback d-block">{rowErrors.peso}</div>}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="wizard-publicaciones-row-detail-card">
                                                            <h5>Envio por zona</h5>
                                                            <div className="wizard-publicaciones-zones-grid">
                                                                {['amba', 'interior', 'patagonia'].map((zone) => (
                                                                    <div key={`${row.id}-${zone}`} className="wizard-publicaciones-zone-card">
                                                                        <label className="form-check">
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                checked={!!row.zonasEnvioGratis?.[zone]}
                                                                                onChange={(event) => handleRowZonaChange(row.id, zone, event.target.checked)}
                                                                            />
                                                                            <span className="form-check-label">
                                                                                {zone === 'amba' ? 'AMBA' : zone === 'interior' ? 'Interior' : 'Patagonia'} gratis
                                                                            </span>
                                                                        </label>

                                                                        <label className="form-label mt-2">
                                                                            Costo fee {zone === 'amba' ? 'AMBA' : zone === 'interior' ? 'Interior' : 'Patagonia'}
                                                                        </label>
                                                                        <input
                                                                            type="number"
                                                                            className={`form-control ${rowErrors?.[`fee_${zone}`] ? 'is-invalid' : ''}`}
                                                                            value={row.costosFee?.[zone] ?? ''}
                                                                            onChange={(event) => handleRowFeeChange(row.id, zone, event.target.value)}
                                                                            min="0"
                                                                            step="0.01"
                                                                            placeholder={defaults.envioPropio ? 'Costo fee' : 'Disponible con envio propio'}
                                                                            disabled={!defaults.envioPropio}
                                                                        />
                                                                        {rowErrors?.[`fee_${zone}`] && (
                                                                            <div className="invalid-feedback d-block">{rowErrors[`fee_${zone}`]}</div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                            {defaults.envioPropio ? (
                                                                <div className="wizard-publicaciones-note wizard-publicaciones-note--neutral mt-3 mb-0">
                                                                    El wizard ahora envía tambien `costoFeeAmba`, `costoFeeInterior` y `costoFeePatagonia`.
                                                                </div>
                                                            ) : (
                                                                <p className="wizard-publicaciones-empty mb-0">
                                                                    Si activas envio propio podras revisar la configuracion por zona.
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                            )}
                                            </React.Fragment>
                                        );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>3. Revision final</h3>
                                    <p>Antes de crear, revisa el resumen del lote y los datos comunes que se van a aplicar.</p>
                                </div>
                            </div>

                            <div className="wizard-publicaciones-summary-grid">
                                <div className="wizard-publicaciones-summary-card">
                                    <span>Total filas</span>
                                    <strong>{summary.total}</strong>
                                </div>
                                <div className="wizard-publicaciones-summary-card">
                                    <span>Filas validas</span>
                                    <strong>{summary.validas}</strong>
                                </div>
                                <div className="wizard-publicaciones-summary-card">
                                    <span>Con error</span>
                                    <strong>{summary.conErrores}</strong>
                                </div>
                            </div>

                            <div className="row g-3 mt-1">
                                <div className="col-12 col-lg-5">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Datos comunes</h4>
                                        <ul className="wizard-publicaciones-review-list">
                                            <li><span>Seller</span><strong>{(selectedSellerLabel ?? selectedSellerId) || '-'}</strong></li>
                                            <li><span>Categoria</span><strong>{selectedCategoriaLabel}</strong></li>
                                            <li><span>Marca</span><strong>{selectedMarcaLabel}</strong></li>
                                            <li><span>Tipo publicacion</span><strong>{selectedTipoLabel}</strong></li>
                                            <li><span>IVA</span><strong>{defaults.alicuotaIva !== '' ? `${defaults.alicuotaIva}%` : '-'}</strong></li>
                                            <li><span>Entrega</span><strong>{defaults.tipoEntrega === '2' ? 'Retiro en sucursal' : defaults.tipoEntrega === '3' ? 'Envio propio' : 'Envio a domicilio / sucursal'}</strong></li>
                                            <li><span>Dimensiones</span><strong>{defaults.replicateDimensions ? 'Replicadas en lote' : 'Por publicacion'}</strong></li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="col-12 col-lg-7">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Preview de filas</h4>
                                        <div className="wizard-publicaciones-review-table">
                                            <table className="table table-sm">
                                                <thead>
                                                    <tr>
                                                        <th>Fila</th>
                                                        <th>SKU</th>
                                                        <th>Titulo</th>
                                                        <th>Precio</th>
                                                        <th>Stock</th>
                                                        <th>Imagenes</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {safeRows.slice(0, 8).map((row, index) => (
                                                        <tr key={row.id}>
                                                            <td>{index + 1}</td>
                                                            <td>{row.skuSeller || '-'}</td>
                                                            <td>{row.titulo || '-'}</td>
                                                            <td>{row.precioLista || '-'}</td>
                                                            <td>{row.stock || '0'}</td>
                                                            <td>{parseImageUrls(row.imagenesTexto).length + (row.imageFiles?.length ?? 0)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {safeRows.length > 8 && (
                                                <div className="wizard-publicaciones-more">
                                                    +{safeRows.length - 8} fila(s) adicionales en el lote
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="wizard-publicaciones-section">
                            <div className="wizard-publicaciones-section-header">
                                <div>
                                    <h3>4. Resultado del procesamiento</h3>
                                    <p>Mostramos el resultado devuelto por el backend actual de creacion masiva.</p>
                                </div>
                            </div>

                            <div className="wizard-publicaciones-summary-grid">
                                <div className="wizard-publicaciones-summary-card">
                                    <span>Creadas</span>
                                    <strong>{createdResults.length}</strong>
                                </div>
                                <div className="wizard-publicaciones-summary-card">
                                    <span>Errores</span>
                                    <strong>{errorResults.length}</strong>
                                </div>
                            </div>

                            <div className="row g-3 mt-1">
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Publicaciones creadas</h4>
                                        {createdResults.length === 0 ? (
                                            <p className="wizard-publicaciones-empty">No hubo altas exitosas en esta ejecucion.</p>
                                        ) : (
                                            <ul className="wizard-publicaciones-results">
                                                {createdResults.map((item, index) => (
                                                    <li key={`${item?.idPublicacion ?? index}-${item?.skuMarket ?? index}`}>
                                                        <strong>{formatApiValue(item?.idPublicacion ?? item?.IdPublicacion, 'Sin ID')}</strong>
                                                        <span>{formatApiValue(item?.skuMarket ?? item?.SkuMarket ?? item?.skuSeller ?? item?.SkuSeller, 'Sin SKU market')}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="col-12 col-xl-6">
                                    <div className="wizard-publicaciones-panel wizard-publicaciones-panel-soft">
                                        <h4>Filas con error</h4>
                                        {errorResults.length === 0 ? (
                                            <p className="wizard-publicaciones-empty">No se reportaron errores para este lote.</p>
                                        ) : (
                                            <ul className="wizard-publicaciones-results wizard-publicaciones-results-danger">
                                                {errorResults.map((item, index) => (
                                                    <li key={`${item?.__rowId ?? item?.skuSeller ?? 'err'}-${index}`}>
                                                        <strong>{getResultErrorTitle(item, index)}</strong>
                                                        {getResultErrorContext(item) && (
                                                            <span className="wizard-publicaciones-result-context">{getResultErrorContext(item)}</span>
                                                        )}
                                                        <span>{formatApiValue(item?.errorMesage ?? item?.ErrorMesage ?? item?.message ?? item?.Message ?? item, 'Error sin detalle')}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="wizard-publicaciones-footer">
                        <div className="wizard-publicaciones-footer-note">
                            El wizard ya contempla imagenes, dimensiones, zonas de envio gratis y costos fee por zona. Esos campos se envian junto con el resto del lote.
                        </div>

                        <div className="wizard-publicaciones-footer-actions">
                            {step > 0 && step < 3 && (
                                <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                                    Volver
                                </button>
                            )}

                            {step < 2 && (
                                <button type="button" className="btn btn-danger" onClick={handleNext}>
                                    Continuar
                                </button>
                            )}

                            {step === 2 && (
                                <>
                                    <button type="button" className="btn btn-outline-secondary" onClick={clearRowsLote}>
                                        Cancelar y vaciar lote
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-danger"
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? 'Creando lote...' : 'Crear publicaciones'}
                                    </button>
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    {errorResults.length > 0 && (
                                        <button type="button" className="btn btn-outline-secondary" onClick={returnToRowsForCorrection}>
                                            Volver al lote para corregir
                                        </button>
                                    )}
                                    <button type="button" className="btn btn-outline-secondary" onClick={resetWizard}>
                                        Crear otro lote
                                    </button>
                                    <button type="button" className="btn btn-danger" onClick={() => navigate('/Publicaciones')}>
                                        Volver al listado
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                
            </div>
        </div>
    );
};

export default CreacionMasivaWizard;
